import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Clock, Play, Square, CheckCircle2, DollarSign, Printer, X, ChevronLeft, ChevronRight, Loader2, Plus, Calendar, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const f$ = (n) => `$${Number(n || 0).toFixed(2)}`;
const fH = (n) => `${Number(n || 0).toFixed(2)}h`;
const fDT = (iso) => iso ? new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

const STATUS_STYLE = {
  open: 'bg-blue-100 text-blue-800',
  closed: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  paid: 'bg-emerald-100 text-emerald-800',
  finalized: 'bg-blue-100 text-blue-800',
  pending: 'bg-amber-100 text-amber-800',
};

const mondayOf = (d) => {
  const dt = new Date(d);
  const day = dt.getUTCDay() || 7;  // Sun = 7
  dt.setUTCDate(dt.getUTCDate() - (day - 1));
  return dt.toISOString().slice(0, 10);
};

const DriverPayrollTab = () => {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [drivers, setDrivers] = useState([]);
  const [preview, setPreview] = useState({ drivers: [], totals: {}, week_start: '', week_end: '' });
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [paystubData, setPaystubData] = useState(null);
  const [showShiftForm, setShowShiftForm] = useState(null); // driver_id or null

  const loadAll = useCallback(async () => {
    const [pre, ru, dr] = await Promise.all([
      fetch(`${API}/api/drivers/payroll/weekly?week_start=${weekStart}`).then(r => r.json()),
      fetch(`${API}/api/drivers/payroll/runs`).then(r => r.json()),
      fetch(`${API}/api/command-center/drivers`).then(r => r.json()),
    ]);
    setPreview(pre); setRuns(ru.runs || []); setDrivers(dr.drivers || []);
  }, [weekStart]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const shiftWeek = (delta) => {
    const d = new Date(weekStart + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + (delta * 7));
    setWeekStart(d.toISOString().slice(0, 10));
  };

  const approveShift = async (shift_id) => {
    setBusy(true);
    await fetch(`${API}/api/drivers/shifts/${shift_id}/approve`, { method: 'POST' });
    toast.success('Shift approved');
    await loadAll();
    setBusy(false);
  };

  const deleteShift = async (shift_id) => {
    if (!window.confirm('Delete this shift?')) return;
    await fetch(`${API}/api/drivers/shifts/${shift_id}`, { method: 'DELETE' });
    toast.success('Shift deleted'); loadAll();
  };

  const clockOut = async (shift) => {
    const tip = window.prompt('Gratuity for this shift? ($)', '0');
    if (tip === null) return;
    await fetch(`${API}/api/drivers/shifts/${shift.shift_id}/clock-out`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gratuity: parseFloat(tip) || 0 }),
    });
    toast.success('Clocked out'); loadAll();
  };

  const runPayroll = async () => {
    if (!preview.totals.all_approved) {
      toast.error('Approve all shifts before running payroll');
      return;
    }
    const email = window.confirm('Run payroll and email paystubs to drivers?');
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/drivers/payroll/run`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ week_start: weekStart, email_statements: email }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Run failed');
      toast.success(`Payroll run ${data.run_id} created · ${f$(data.totals.total_payable)} total`);
      await loadAll();
      setSelectedRun(data);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const markPaid = async (run_id, driver_id) => {
    const ref = window.prompt('Payment reference (ACH/check #)?', '');
    if (ref === null) return;
    await fetch(`${API}/api/drivers/payroll/runs/${run_id}/mark-paid`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ driver_id, payment_reference: ref }),
    });
    toast.success('Marked paid'); loadAll();
    // Refresh the open run
    if (selectedRun && selectedRun.run_id === run_id) {
      const refreshed = await fetch(`${API}/api/drivers/payroll/runs/${run_id}`).then(r => r.json());
      setSelectedRun(refreshed);
    }
  };

  const viewPaystub = async (run_id, driver_id) => {
    const data = await fetch(`${API}/api/drivers/payroll/runs/${run_id}/paystub/${driver_id}`).then(r => r.json());
    setPaystubData(data);
  };

  return (
    <Card className="p-4 sm:p-6 bg-white" data-testid="driver-payroll-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-red-600" />
          <h2 className="text-xl font-bold tracking-tight">Driver Hours & Payroll</h2>
          <span className="text-xs text-gray-500 ml-2">Weekly Mon–Sun · 1099 flat rate</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => shiftWeek(-1)} data-testid="prev-week"><ChevronLeft className="h-3 w-3" /></Button>
          <Input type="date" value={weekStart} onChange={e => setWeekStart(mondayOf(e.target.value))} className="h-8 w-36" data-testid="week-start" />
          <Button size="sm" variant="outline" onClick={() => shiftWeek(1)} data-testid="next-week"><ChevronRight className="h-3 w-3" /></Button>
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-4">Week of <strong>{preview.week_start}</strong> → <strong>{preview.week_end}</strong> ({preview.week_label})</p>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        <Tile label="Drivers" value={preview.totals.drivers || 0} />
        <Tile label="Hours" value={fH(preview.totals.total_hours)} />
        <Tile label="Gross" value={f$(preview.totals.total_gross)} accent="green" />
        <Tile label="Gratuity" value={f$(preview.totals.total_gratuity)} accent="blue" />
        <Tile label="Total Payable" value={f$(preview.totals.total_payable)} accent="red" />
      </div>

      {/* Run payroll banner */}
      <div className={`border rounded-lg p-4 mb-5 flex items-center justify-between flex-wrap gap-3 ${preview.totals.all_approved && preview.totals.drivers ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-testid="run-payroll-banner">
        <div className="flex items-start gap-2">
          {preview.totals.all_approved && preview.totals.drivers ? <CheckCircle2 className="h-5 w-5 text-green-700" /> : <AlertTriangle className="h-5 w-5 text-amber-700" />}
          <div>
            <p className="text-sm font-bold">
              {preview.totals.drivers === 0 ? 'No shifts logged this week' :
                preview.totals.all_approved ? 'Ready to run payroll' : 'Approve all shifts before running'}
            </p>
            <p className="text-xs text-gray-600 mt-0.5">
              {preview.totals.drivers} driver(s) · {f$(preview.totals.total_payable)} total payable
            </p>
          </div>
        </div>
        <Button onClick={runPayroll} disabled={busy || !preview.totals.all_approved || preview.totals.drivers === 0} className="bg-red-600 hover:bg-red-700 font-bold" data-testid="run-payroll-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <DollarSign className="h-4 w-4 mr-1" />} Run Payroll
        </Button>
      </div>

      {/* Per-driver breakdown */}
      <div className="space-y-3 mb-6" data-testid="driver-breakdowns">
        {preview.drivers.length === 0 && (
          <p className="text-sm text-gray-400 italic py-8 text-center border border-dashed rounded">No driver activity in this week. Use "Add Shift" below for any driver.</p>
        )}
        {preview.drivers.map(d => (
          <DriverWeekCard
            key={d.driver_id}
            driver={d}
            onApprove={approveShift}
            onDelete={deleteShift}
            onClockOut={clockOut}
          />
        ))}
      </div>

      {/* Quick add shift for any driver */}
      <div className="border-t pt-5 mb-5">
        <h3 className="text-sm font-bold tracking-tight mb-3">Log a Shift</h3>
        <div className="flex flex-wrap gap-2">
          {drivers.map(d => (
            <button
              key={d.driver_id}
              onClick={() => setShowShiftForm(d.driver_id)}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-full hover:border-red-600 hover:text-red-600 transition-colors"
              data-testid={`add-shift-${d.driver_id}`}
            >
              <Plus className="h-3 w-3 inline mr-1" /> {d.name}
            </button>
          ))}
          {drivers.length === 0 && <p className="text-xs text-gray-400 italic">Add drivers in Drivers tab first.</p>}
        </div>
      </div>

      {/* Past runs */}
      <div className="border-t pt-5">
        <h3 className="text-sm font-bold tracking-tight mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-gray-500" /> Past Payroll Runs</h3>
        {runs.length === 0 ? (
          <p className="text-xs text-gray-400 italic text-center py-4">No runs yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payroll-runs-table">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                <tr><th className="text-left px-3 py-2">Run</th><th className="text-left px-3 py-2">Week</th><th className="text-right px-3 py-2">Hours</th><th className="text-right px-3 py-2">Drivers</th><th className="text-right px-3 py-2">Payable</th><th className="text-center px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.run_id} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs">{r.run_id}</td>
                    <td className="px-3 py-2 text-xs">{r.week_label} ({r.week_start} → {r.week_end})</td>
                    <td className="px-3 py-2 text-right">{fH(r.totals?.total_hours)}</td>
                    <td className="px-3 py-2 text-right">{r.totals?.drivers}</td>
                    <td className="px-3 py-2 text-right font-bold">{f$(r.totals?.total_payable)}</td>
                    <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLE[r.status]}`}>{r.status.toUpperCase()}</span></td>
                    <td className="px-3 py-2 text-right"><button onClick={() => setSelectedRun(r)} className="text-xs text-blue-600 hover:underline">Open</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showShiftForm && (
        <ShiftFormModal
          driverId={showShiftForm}
          driverName={(drivers.find(d => d.driver_id === showShiftForm) || {}).name}
          onClose={() => setShowShiftForm(null)}
          onSaved={() => { setShowShiftForm(null); loadAll(); }}
        />
      )}

      {selectedRun && (
        <RunDetailModal
          run={selectedRun}
          onClose={() => setSelectedRun(null)}
          onMarkPaid={markPaid}
          onViewPaystub={(d) => viewPaystub(selectedRun.run_id, d.driver_id)}
        />
      )}

      {paystubData && (
        <PaystubModal paystub={paystubData} onClose={() => setPaystubData(null)} />
      )}
    </Card>
  );
};

// ─── Per-driver weekly breakdown card ─────────────────────────────────────
const DriverWeekCard = ({ driver: d, onApprove, onDelete, onClockOut }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`driver-card-${d.driver_id}`}>
    <div className="bg-gray-50 px-4 py-2 flex items-center justify-between flex-wrap gap-2">
      <div>
        <p className="font-bold text-sm">{d.name}</p>
        <p className="text-[10px] text-gray-500">{d.email} · {f$(d.hourly_rate)}/hr · {d.pay_method}</p>
      </div>
      <div className="text-right">
        <p className="text-[10px] uppercase tracking-widest text-gray-500">Total Payable</p>
        <p className="text-xl font-black text-green-700">{f$(d.total_payable)}</p>
      </div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-white text-[10px] uppercase tracking-wider text-gray-500">
          <tr><th className="text-left px-3 py-1.5">Start</th><th className="text-left px-3 py-1.5">End</th><th className="text-right px-3 py-1.5">Hours</th><th className="text-right px-3 py-1.5">Gross</th><th className="text-right px-3 py-1.5">Tip</th><th className="text-center px-3 py-1.5">Status</th><th className="text-right px-3 py-1.5"></th></tr>
        </thead>
        <tbody>
          {d.shifts.map(s => (
            <tr key={s.shift_id} className="border-t">
              <td className="px-3 py-2 text-xs">{fDT(s.start_at)}{s.notes && <div className="text-[10px] text-gray-400">{s.notes}</div>}</td>
              <td className="px-3 py-2 text-xs">{s.end_at ? fDT(s.end_at) : <span className="text-amber-700 font-semibold">— open —</span>}</td>
              <td className="px-3 py-2 text-right">{fH(s.hours)}</td>
              <td className="px-3 py-2 text-right">{f$(s.hours * s.hourly_rate)}</td>
              <td className="px-3 py-2 text-right text-blue-700">{f$(s.gratuity)}</td>
              <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLE[s.status]}`}>{s.status.toUpperCase()}</span></td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {s.status === 'open' && <button onClick={() => onClockOut(s)} className="text-xs text-blue-600 hover:underline mr-2" data-testid={`clock-out-${s.shift_id}`}>Clock out</button>}
                {s.status === 'closed' && <button onClick={() => onApprove(s.shift_id)} className="text-xs text-green-700 hover:underline mr-2" data-testid={`approve-${s.shift_id}`}>Approve</button>}
                {s.status !== 'paid' && <button onClick={() => onDelete(s.shift_id)} className="text-[10px] text-red-600 hover:underline">×</button>}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-black bg-gray-50 font-bold">
            <td className="px-3 py-2 text-xs" colSpan="2">Subtotal</td>
            <td className="px-3 py-2 text-right">{fH(d.total_hours)}</td>
            <td className="px-3 py-2 text-right">{f$(d.gross_pay)}</td>
            <td className="px-3 py-2 text-right text-blue-700">{f$(d.gratuity_total)}</td>
            <td colSpan="2" className="px-3 py-2 text-right text-green-700">{f$(d.total_payable)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
);

// ─── Add shift modal ──────────────────────────────────────────────────────
const ShiftFormModal = ({ driverId, driverName, onClose, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today, start: '09:00', end: '17:00', gratuity: 0, notes: '', booking_id: '',
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.start) { toast.error('Start time required'); return; }
    setBusy(true);
    try {
      const start_at = new Date(`${form.date}T${form.start}:00`).toISOString();
      const end_at = form.end ? new Date(`${form.date}T${form.end}:00`).toISOString() : null;
      const r = await fetch(`${API}/api/drivers/${driverId}/shifts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_at, end_at, gratuity: parseFloat(form.gratuity) || 0, notes: form.notes, booking_id: form.booking_id }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Save failed');
      toast.success('Shift logged');
      onSaved();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="shift-form-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Log Shift · {driverName}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          <FieldRow label="Date"><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="shift-date" /></FieldRow>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Start time"><Input type="time" value={form.start} onChange={e => setForm({ ...form, start: e.target.value })} data-testid="shift-start" /></FieldRow>
            <FieldRow label="End time (optional)"><Input type="time" value={form.end} onChange={e => setForm({ ...form, end: e.target.value })} data-testid="shift-end" /></FieldRow>
          </div>
          <FieldRow label="Gratuity ($)"><Input type="number" step="0.01" value={form.gratuity} onChange={e => setForm({ ...form, gratuity: e.target.value })} data-testid="shift-gratuity" /></FieldRow>
          <FieldRow label="Linked booking ID (optional)"><Input value={form.booking_id} onChange={e => setForm({ ...form, booking_id: e.target.value })} placeholder="BK-XXXXXXXX" /></FieldRow>
          <FieldRow label="Notes"><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Airport run, etc." /></FieldRow>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="shift-save">
            {busy ? 'Saving…' : 'Log Shift'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Run detail modal ────────────────────────────────────────────────────
const RunDetailModal = ({ run, onClose, onMarkPaid, onViewPaystub }) => (
  <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="run-detail-modal">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-red-600 font-bold">Payroll Run</p>
          <h3 className="text-xl font-black tracking-tight">{run.week_label}</h3>
          <p className="text-xs text-gray-500">{run.week_start} → {run.week_end} · {f$(run.totals?.total_payable)} total · {run.totals?.drivers} drivers</p>
        </div>
        <button onClick={onClose}><X className="h-5 w-5 text-gray-400" /></button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
            <tr><th className="text-left px-3 py-2">Driver</th><th className="text-right px-3 py-2">Hours</th><th className="text-right px-3 py-2">Gross</th><th className="text-right px-3 py-2">Tip</th><th className="text-right px-3 py-2">Payable</th><th className="text-center px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {(run.breakdowns || []).map(d => (
              <tr key={d.driver_id} className="border-t">
                <td className="px-3 py-2">
                  <div className="font-semibold">{d.name}</div>
                  <div className="text-[10px] text-gray-500">{d.pay_method} · {d.email}</div>
                </td>
                <td className="px-3 py-2 text-right">{fH(d.total_hours)}</td>
                <td className="px-3 py-2 text-right">{f$(d.gross_pay)}</td>
                <td className="px-3 py-2 text-right text-blue-700">{f$(d.gratuity_total)}</td>
                <td className="px-3 py-2 text-right font-bold text-green-700">{f$(d.total_payable)}</td>
                <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLE[d.status || 'pending']}`}>{(d.status || 'pending').toUpperCase()}</span></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => onViewPaystub(d)} className="text-xs text-blue-600 hover:underline mr-3">Paystub</button>
                  {d.status !== 'paid' && <button onClick={() => onMarkPaid(run.run_id, d.driver_id)} className="text-xs text-green-700 hover:underline" data-testid={`mark-paid-${d.driver_id}`}>Mark Paid</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// ─── Paystub modal ───────────────────────────────────────────────────────
const PaystubModal = ({ paystub, onClose }) => {
  const { run, breakdown, shifts } = paystub;
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4 print:bg-white print:p-0" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-xl w-full max-h-[92vh] overflow-y-auto p-8 print:max-w-none print:rounded-none" onClick={e => e.stopPropagation()} data-testid="paystub-modal">
        <div className="flex items-center justify-between mb-5 print:hidden">
          <h3 className="font-bold flex items-center gap-2"><FileText className="h-5 w-5 text-red-600" /> Paystub</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => window.print()}><Printer className="h-4 w-4" /></Button>
            <button onClick={onClose} className="text-2xl text-gray-400">×</button>
          </div>
        </div>
        <div className="border-b border-gray-200 pb-4 mb-4">
          <p className="text-[10px] uppercase tracking-widest text-red-600 font-bold">ACE · Driver Paystub</p>
          <h2 className="text-2xl font-black tracking-tight mt-1">{breakdown.name}</h2>
          <p className="text-xs text-gray-500 mt-1">{run.week_label} · {run.week_start} → {run.week_end} · Paid via {breakdown.pay_method}</p>
        </div>
        <table className="w-full text-sm mb-5">
          <tbody>
            <tr><td className="py-1.5 text-gray-500">Hours worked</td><td className="text-right font-bold">{fH(breakdown.total_hours)}</td></tr>
            <tr><td className="py-1.5 text-gray-500">Hourly rate</td><td className="text-right">{f$(breakdown.hourly_rate)}/hr</td></tr>
            <tr><td className="py-1.5 text-gray-500">Gross pay</td><td className="text-right">{f$(breakdown.gross_pay)}</td></tr>
            <tr><td className="py-1.5 text-gray-500">Gratuity</td><td className="text-right">{f$(breakdown.gratuity_total)}</td></tr>
            <tr className="border-t-2 border-black"><td className="py-2 font-bold">Total payable</td><td className="text-right font-black text-lg text-green-700">{f$(breakdown.total_payable)}</td></tr>
          </tbody>
        </table>
        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Shifts included ({shifts.length})</h4>
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-gray-500">
            <tr><th className="text-left py-1">Start</th><th className="text-left py-1">End</th><th className="text-right py-1">Hours</th><th className="text-right py-1">Tip</th></tr>
          </thead>
          <tbody>
            {shifts.map(s => (
              <tr key={s.shift_id} className="border-t">
                <td className="py-1.5">{fDT(s.start_at)}</td>
                <td className="py-1.5">{fDT(s.end_at)}</td>
                <td className="py-1.5 text-right">{fH(s.hours)}</td>
                <td className="py-1.5 text-right text-blue-700">{f$(s.gratuity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-[10px] text-gray-400 mt-6 text-center">
          1099-NEC issued at year-end if YTD earnings exceed $600 · ACE Auto Concierge · Atlanta, GA
        </p>
      </div>
    </div>
  );
};

const Tile = ({ label, value, accent }) => {
  const map = { red: 'text-red-700 border-red-200 bg-red-50', amber: 'text-amber-800 border-amber-200 bg-amber-50', green: 'text-green-700 border-green-200 bg-green-50', blue: 'text-blue-700 border-blue-200 bg-blue-50' };
  return (
    <div className={`border rounded-lg px-3 py-2 ${map[accent] || 'bg-gray-50 border-gray-200'}`}>
      <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</p>
      <p className="mt-0.5 text-xl font-black">{value}</p>
    </div>
  );
};

const FieldRow = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default DriverPayrollTab;
